import React, { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle2, Circle } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import ToolPageLayout from '../../components/ToolPageLayout';
import { useLanguage } from '../../contexts/LanguageContext';

const FREQ_OPTIONS = [
  { key: '1', labelKey: 'atb_freq_1', hours: [8] },
  { key: '2', labelKey: 'atb_freq_2', hours: [8, 20] },
  { key: '3', labelKey: 'atb_freq_3', hours: [8, 14, 20] },
  { key: '4', labelKey: 'atb_freq_4', hours: [8, 13, 18, 23] },
];

const pad = (n) => String(n).padStart(2, '0');

const AntibioticPlanner = () => {
  const { t, isRTL } = useLanguage();
  const [name, setName] = useState('');
  const [freqKey, setFreqKey] = useState('3');
  const [duration, setDuration] = useState('');
  const [startDate, setStartDate] = useState('');
  const [checkedDoses, setCheckedDoses] = useState({});

  const freq = FREQ_OPTIONS.find((f) => f.key === freqKey);
  const days = parseInt(duration, 10);
  const valid = days > 0 && days <= 30 && startDate;

  const storageKey = `tabbibak_atb_${name || 'traitement'}_${startDate}_${freqKey}_${duration}`;

  useEffect(() => {
    if (!valid) return;
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) setCheckedDoses(JSON.parse(saved));
      else setCheckedDoses({});
    } catch {
      // localStorage indisponible, on ignore
    }
  }, [storageKey, valid]);

  const toggleDose = (doseKey) => {
    const next = { ...checkedDoses, [doseKey]: !checkedDoses[doseKey] };
    setCheckedDoses(next);
    try {
      localStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
      // ignore
    }
  };

  let schedule = [];
  if (valid) {
    const [y, m, d] = startDate.split('-').map(Number);
    for (let dayIdx = 0; dayIdx < days; dayIdx++) {
      const date = new Date(Date.UTC(y, m - 1, d + dayIdx));
      const dateLabel = date.toLocaleDateString(isRTL ? 'ar-DZ-u-nu-latn' : 'fr-FR', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
      const doses = freq.hours.map((h) => ({
        key: `${dayIdx}-${h}`,
        time: `${pad(h)}h00`,
      }));
      schedule.push({ dateLabel, doses });
    }
  }

  const totalDoses = valid ? days * freq.hours.length : 0;
  const takenDoses = Object.values(checkedDoses).filter(Boolean).length;

  return (
    <ToolPageLayout
      title={t('atb_title')}
      subtitle={t('atb_subtitle')}
      specialty="generaliste"
    >
      <div className="bg-[#FEF3C7] border border-[#F59E0B]/30 rounded-2xl p-4 flex gap-3 mb-8">
        <AlertTriangle className="w-5 h-5 text-[#F59E0B] shrink-0 mt-0.5" />
        <p className="text-sm text-[#92400E]">
          {t('atb_warning')}
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mb-6">
        <div>
          <Label className="mb-1 block">{t('atb_name_label')}</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('atb_name_placeholder')} className="rounded-xl" />
        </div>
        <div>
          <Label className="mb-1 block">{t('atb_start_date_label')}</Label>
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="rounded-xl" />
        </div>
        <div>
          <Label className="mb-1 block">{t('atb_frequency_label')}</Label>
          <select
            value={freqKey}
            onChange={(e) => setFreqKey(e.target.value)}
            className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm bg-white h-10 focus:outline-none focus:ring-2 focus:ring-[#0596DE]"
          >
            {FREQ_OPTIONS.map((f) => (
              <option key={f.key} value={f.key}>{t(f.labelKey)}</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="mb-1 block">{t('atb_duration_label')}</Label>
          <Input type="number" min="1" max="30" value={duration} onChange={(e) => setDuration(e.target.value)} placeholder={t('atb_duration_placeholder')} className="rounded-xl" />
        </div>
      </div>

      {valid && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-[#0F172A]">
              {t('atb_schedule_heading')} {name && `— ${name}`}
            </h3>
            <span className="text-sm text-[#64748B]">{takenDoses} / {totalDoses} {t('atb_doses_checked')}</span>
          </div>
          <div className="space-y-4">
            {schedule.map((day, di) => (
              <div key={di} className="bg-white border border-[#E2E8F0] rounded-xl p-4">
                <p className="text-sm font-semibold text-[#0F172A] mb-2 capitalize">{day.dateLabel}</p>
                <div className="flex flex-wrap gap-2">
                  {day.doses.map((dose) => {
                    const done = !!checkedDoses[dose.key];
                    return (
                      <button
                        key={dose.key}
                        type="button"
                        onClick={() => toggleDose(dose.key)}
                        className={`inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-full border transition-colors ${done ? 'bg-[#F0FDF4] border-[#10B981]/40 text-[#065F46]' : 'bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]'}`}
                      >
                        {done ? <CheckCircle2 className="w-4 h-4 text-[#10B981]" /> : <Circle className="w-4 h-4 text-[#94A3B8]" />}
                        {dose.time}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="mt-6 text-sm text-[#64748B]">
        {t('atb_disclaimer')}
      </p>
    </ToolPageLayout>
  );
};

export default AntibioticPlanner;
